import { useEffect, useState } from "react";
import { routes } from "@/constants/routes";
import { Image } from "components/Image";
import Logo from "assets/images/cached-welcome_logo.svg";
import BroughtToYou from "assets/images/brought_to_you.png";
import { WELCOME_PAGE } from "constants/AllStaticText";
import profileIcon from "assets/images/profile_dark_icon.svg";
import { VideoWithThumbnail } from "components/VideoWithThumbnail";
import { Button } from "components/Button";
import { BTN_SIZE, BTN_VARIANTS } from "constants/components";
import {
  EXISTING_USER,
  menuItems,
  ONBOARDING_DONE,
  WELCOME_OFFER,
} from "constants/general";
import {
  getFromLocalStorage,
  pushToDataLayer,
  setToLocalStorage,
} from "core/utils/utils.helper";
import { CONTENT } from "constants/dataLayer";
import parse from "html-react-parser";
import { Typography } from "components/Typography";
import {
  ONBOARDING_NAV_PAGES,
  WELCOME_VIDEO_DATA,
} from "@/constants/welcome-intro";
import { Link, useNavigate } from "react-router-dom";
import { OnboardingProgressData } from "@/index-db/db.types";
import {
  getOnboardingProgress,
  saveOnboardingProgress,
} from "@/helpers/onboarding.helper";
import { Loader } from "@/components/Loader";
import styles from "./Welcome.module.scss";
import useAuth from "@/hooks/useAuth";
import { useModal } from "@/hooks/useModal";
import { ModalType } from "@/models/common";
import { STORAGE_WELCOME_VOUCHER_STAGE } from "@/constants/storage";
import asyncComponentLoader from "core/loader";
import { useCountry } from "@/hooks/useCountry";
import React from "react";
import useMediaQuery from "@/hooks/useMediaQuery";
import { ScreenSize } from "@/models/screenSize";
import {
  getOnboardingVoucherModalWidth,
  isOnboardingVoucherEnabled,
} from "@/helpers/onboardingVoucher.helper";

const OnboardingVoucherPopup = asyncComponentLoader(() =>
  import("components/OnboardingVoucherPopup").then((module) => ({
    default: module.OnboardingVoucherPopup,
  })),
);

export const Welcome: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true); // To store loading status of fetching current page from index db
  const [navState, setNavState] = useState<OnboardingProgressData>({
    currentPage: ONBOARDING_NAV_PAGES.WELCOME,
    introSlideIndex: 0,
  }); // To store onboarding progress data

  const navigate = useNavigate();

  const auth = useAuth();
  const isLoggedIn = auth && auth.isAuthenticated;

  const { country } = useCountry();

  const { showModal, closeModal } = useModal(); // Modal methods

  // To update index db and navState
  const handleNextBtnClick = () => {
    const newCurrentPage = ONBOARDING_NAV_PAGES.INTRO;
    // Save progress to index db
    saveOnboardingProgress({ ...navState, currentPage: newCurrentPage });
    // Update navState and will trigger re-render, and redirect to next page
    setNavState((state) => ({
      ...state,
      currentPage: newCurrentPage,
    }));
  };

  // Get onboarding progress from index db
  const fetchNavStateFromIndexDb = () => {
    getOnboardingProgress()
      .then((saved) => {
        if (saved?.[0]) {
          setNavState(saved[0]); // Update navState and trigger redirect by invoking the useEffect below
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(
          "An error occured while fetching data from index db",
          err,
        );
        setIsLoading(false);
      });
  };

  // Dismiss the voucher modal and mark the offer as shown in localStorage
  const closeVoucherModal = () => {
    setToLocalStorage(
      STORAGE_WELCOME_VOUCHER_STAGE,
      WELCOME_OFFER.STATUS.OFFER_SHOWN,
    );
    closeModal(WELCOME_OFFER.MODAL_ID);
  };

  // Close the voucher modal and navigate to sign-up when the user clicks "Sign up & claim"
  const signUpAndClaimVoucher = () => {
    closeVoucherModal();
    navigate(routes.signup.path);
    pushToDataLayer({
      ...CONTENT.VOUCHER_POPUP.SIGNUP,
      user_status: false,
    });
  };

  const voucherMaybeLater = () => {
    closeVoucherModal();
    pushToDataLayer({
      ...CONTENT.VOUCHER_POPUP.MAY_BE_LATER,
      user_status: false,
    });
  };

  // Show the pre-login voucher offer modal to new unauthenticated users
  const showVoucherModal = () => {
    showModal({
      uId: WELCOME_OFFER.MODAL_ID, // Modal Id
      children: (
        <OnboardingVoucherPopup
          showVoucher={false}
          onSignUpClaim={signUpAndClaimVoucher}
          onMaybeLater={voucherMaybeLater}
        />
      ),
      type: ModalType.CUSTOM,
      onCancel: closeVoucherModal,
      closable: true,
      width: getOnboardingVoucherModalWidth(),
    });
  };

  useEffect(() => {
    // Skip if the country is unknown or the onboarding voucher feature is not enabled for it.
    if (!country || !isOnboardingVoucherEnabled(country)) {
      return;
    }

    const isExistingUser = getFromLocalStorage(EXISTING_USER) === "true";
    // Stop voucher modal from showing for existing/logged in user
    if (isExistingUser || isLoggedIn) {
      return;
    }

    const welcomeVoucherStage = getFromLocalStorage(
      STORAGE_WELCOME_VOUCHER_STAGE,
    );
    // Stop voucher modal from showing if already shown
    if (
      welcomeVoucherStage === WELCOME_OFFER.STATUS.OFFER_SHOWN ||
      welcomeVoucherStage === WELCOME_OFFER.STATUS.VOUCHER_SHOWN
    ) {
      return;
    }

    showVoucherModal();
  }, [isLoggedIn, country]);

  useEffect(() => {
    fetchNavStateFromIndexDb();
  }, []);

  // Handle redirection when navState changes either by index db stored data or next button click
  useEffect(() => {
    if (isLoggedIn) {
      return;
    }
    const isExistingUser = getFromLocalStorage(EXISTING_USER) === "true";
    if (isExistingUser) {
      navigate(routes.loginIntro.path);
      return;
    }
    const hasOnboardingCompleted =
      getFromLocalStorage(ONBOARDING_DONE) === "true";
    // if the onboarding is completed then no need to show the intro screens,
    if (hasOnboardingCompleted) {
      navigate(routes.signupIntro.path);
      return;
    }
    if (navState.currentPage === ONBOARDING_NAV_PAGES.INTRO) {
      navigate(routes.intro.path);
      return;
    }
    if (navState.currentPage === ONBOARDING_NAV_PAGES.TAPTOSTART) {
      navigate(`${routes.home.path}/${routes.tapToStart.path}`);
    }
  }, [navState.currentPage]);

  return isLoading ? (
    <div className={styles.loader_container}>
      <Loader loaderText="Loading" />
    </div>
  ) : (
    <div className={styles.welcome_container}>
      {/* Top section: Logo */}
      <div className={styles.logo_section}>
        <div className={styles.container_fluid}>
          <Image
            altText={WELCOME_PAGE.LOGO_ALT}
            source={Logo}
            className={styles.welcome_logo}
          />
        </div>
      </div>
      <div className={styles.content_main}>
        {/* Welcome text section */}
        <div className={styles.welcome_section}>
          <h1 className={styles.welcome_text}>
            <span className={styles.sub_text}>
              {WELCOME_PAGE.HEADING.PART_1}
            </span>
            <br />
            <span className={styles.main_text}>
              {WELCOME_PAGE.HEADING.PART_2}
            </span>
          </h1>
          <div className={styles.line_divider} />
        </div>
        {/* Brand info image section */}
        <div className={styles.brand_info_section}>
          <Image
            altText={WELCOME_PAGE.BROUGHT_TO_YOU_ALT}
            source={BroughtToYou}
            className={styles.brand_info_image}
          />
        </div>
        {/* Video section */}
        <div className={styles.intro_video_section}>
          <div className={styles.intro_video_wrapper}>
            <VideoWithThumbnail
              isCompletedStatusNeeded={false}
              data={WELCOME_VIDEO_DATA}
            />
          </div>
        </div>
        {/* Description */}
        <p className={styles.description_text}>{WELCOME_PAGE.DESCRIPTION}</p>
        {/* Action buttons section */}
        <div className={styles.action_btn_container}>
          <Button
            size={BTN_SIZE.LARGE}
            type={BTN_VARIANTS.PRIMARY}
            className={styles.btn_get_started}
            onClick={() => {
              handleNextBtnClick();
              pushToDataLayer({
                ...CONTENT.WELCOME_PAGE.GET_STARTED,
                user_status: false,
              });
            }}
          >
            {WELCOME_PAGE.ACTION_BUTTONS.GET_STARTED}
          </Button>
          <Button
            size={BTN_SIZE.NEUTRAL}
            type={BTN_VARIANTS.LINK}
            className={styles.btn_login}
            onClick={() => {
              navigate(routes.loginIntro.path);
              pushToDataLayer({
                ...CONTENT.WELCOME_PAGE.LOGIN,
                user_status: false,
              });
            }}
          >
            {WELCOME_PAGE.ACTION_BUTTONS.LOGIN}
          </Button>
        </div>
        {/* Footer section */}
        <div className={styles.footer_container}>
          <div className={styles.content_section}>
            <Typography variant="div" className={styles.content} title={""}>
              {parse(WELCOME_PAGE.FOOTER.DESCRIPTION)}
            </Typography>
          </div>
          <div className={styles.btn_wrapper}>
            <Link
              to={menuItems.PROFILE.to}
              className={styles.profile_btn}
              onClick={() => {
                pushToDataLayer({
                  ...CONTENT.GENERAL.PROFILE,
                  user_status: false,
                });
              }}
            >
              <Image source={profileIcon} altText="" />
              <span>{WELCOME_PAGE.FOOTER.PROFILE_BUTTON}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
